import { useMemo } from 'react';
import { JSONContent } from '@tiptap/core';

import { ResponsiveImage } from 'approot/shared/images/imgix-responsive.constants';
import type { LessonActivitiesFragment } from '../lesson-builder.graphql.generated';
import { PRIVATE_CLASS } from 'global/private-routes.constants';
import { getClassToDoOrStudentClassToDoId } from 'approot/student-class-to-do/student-class-to-do.utils';
import {
  ConclusionCompletionCheck,
  ConclusionContentImage,
  CSInSchoolsConclusion,
} from '../conclusion/activity-builder-conclusion-slide';
import { useAccessibleHeading } from 'approot/shared/ally/accessible-heading';
import { useCsInSchool } from 'approot/shared/authors/authors.hooks';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import {
  ConclusionView,
  ConclusionWrapper,
} from '../conclusion/activity-builder-conclusion.style';
import {
  StudentConclusionButton,
  StudentConclusionChoiceContainer,
  StudentConclusionChoicePrimaryAction,
  StudentConclusionChoiceSecondaryAction,
  StudentConclusionOptionContainer,
} from './student-conclusion.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import { Heading20 } from 'approot/shared/styles/typography.style';
import Text from 'approot/shared/styles/typography/text';
import ImageContentEye from 'approot/shared/images/content/image-content-eye';
import ImageContentSend from 'approot/shared/images/content/image-content-send';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import StudentConfirmSubmitModal from './student-confirm-submit-modal';
import type { Pluck } from 'lib/gql/pluck';

const StudentActivityBuilderConclusionView = ({
  focusQuestion,
  phenomena,
  assessmentName,
  contentName,
  responsiveImages,
  authorId,
  onSubmit,
  isSubmitting,
  onCheckMyWork,
}: {
  focusQuestion?: Pluck<LessonActivitiesFragment, 'focusQuestion'> | null;
  phenomena?: Pluck<LessonActivitiesFragment, 'phenomena'> | null;
  assessmentName?: JSONContent | null;
  contentName: string;
  responsiveImages: ResponsiveImage[];
  authorId: number;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  onCheckMyWork?: () => void;
}) => {
  const classId = useMemo(() => getClassToDoOrStudentClassToDoId(), []);
  const classIdentifier = classId ? classId.toLocaleLowerCase() : '';
  const { registerHeading } = useAccessibleHeading();
  const { isCsInSchoolsLesson } = useCsInSchool(authorId);

  const {
    isVisible: isConfirmSubmitModalVisible,
    openModal: openConfirmSubmitModal,
    closeModal: closeConfirmSubmitModal,
  } = useSimpleModalState();

  if (onSubmit) {
    return (
      <>
        {isConfirmSubmitModalVisible && (
          <StudentConfirmSubmitModal
            onSubmit={onSubmit}
            onClose={closeConfirmSubmitModal}
            isSubmitting={isSubmitting}
          />
        )}
        <StudentConclusionOptionContainer>
          <ScreenReaderOnly as="h1" tabIndex={-1} ref={registerHeading}>
            {`Conclusion slide for ${contentName}`}
          </ScreenReaderOnly>
          <Box $flexDirection="column" $alignItems="center" $maxWidth={320}>
            <Heading20 as="h2" textAlignCenter marginBottom={8}>
              Before you submit
            </Heading20>
            <Text $colour="grey" $align="center">
              Check your answers, then hit submit when you&apos;re ready.
            </Text>
          </Box>
          <Box $gap={24}>
            <StudentConclusionChoiceContainer
              type="button"
              disabled={isSubmitting}
              onClick={onCheckMyWork}
            >
              <ImageContentEye width={120} altText="Eye icon" />
              <StudentConclusionChoiceSecondaryAction>
                Check my work
              </StudentConclusionChoiceSecondaryAction>
            </StudentConclusionChoiceContainer>
            <StudentConclusionChoiceContainer
              type="button"
              disabled={isSubmitting}
              onClick={openConfirmSubmitModal}
            >
              <ImageContentSend width={120} altText="Send icon" />
              <StudentConclusionChoicePrimaryAction>
                Submit
              </StudentConclusionChoicePrimaryAction>
            </StudentConclusionChoiceContainer>
          </Box>
        </StudentConclusionOptionContainer>
      </>
    );
  }

  return (
    <ConclusionView>
      <ScreenReaderOnly as="h1" tabIndex={-1} ref={registerHeading}>
        {`Conclusion slide for ${contentName}`}
      </ScreenReaderOnly>
      <ConclusionWrapper aria-hidden $isSmall={isCsInSchoolsLesson}>
        <ConclusionContentImage
          focusQuestion={focusQuestion}
          phenomena={phenomena}
          assessmentName={assessmentName}
          responsiveImages={responsiveImages}
        />
        <ConclusionCompletionCheck />
      </ConclusionWrapper>

      {isCsInSchoolsLesson && <CSInSchoolsConclusion />}

      <StudentConclusionButton
        to={PRIVATE_CLASS.replace(':classId', classIdentifier)}
        $isXLarge
        $borderRadius={40}
      >
        Exit
      </StudentConclusionButton>
    </ConclusionView>
  );
};

export default StudentActivityBuilderConclusionView;
